import {makeAutoObservable, runInAction} from "mobx";
import AccountService from "../../../services/api/account-service";
import {rootContainer, TYPE} from "../../../services/container/root";
import AccountEditorStore from "./account-editor-store";
import { PERMISSION } from "../../../models/PermissionsTypes";
import IframeService from "../../../services/iframe-service/iframe-service";
import {IAccountAclsModel} from "../../../models/Account";

class CurrentAccountStore {
    // Assigned in constructor so makeAutoObservable works with useDefineForClassFields.
    private _account?: AccountEditorStore;
    private _isSysAdmin: boolean;
    private _acls: IAccountAclsModel[];
    private _isLoading: boolean;

    public get account(): AccountEditorStore | undefined {
        return this._account;
    }
    public get isLoading(): boolean {
        return this._isLoading;
    }

    constructor() {
        this._account = undefined;
        this._isSysAdmin = false;
        this._acls = [];
        this._isLoading = false;
        makeAutoObservable(this);
    }

    private get service(): AccountService {
        return rootContainer.get<AccountService>(TYPE.AccountService);
    }

    private get _iframeService(): IframeService {
        return rootContainer.get<IframeService>(TYPE.IframService);
    }

    public async load() {
        this._isLoading = true;

        try {
            const profile = await this.service.loadCurrentUserProfileData();
            runInAction(() => {
                this._isLoading = false;
                this._isSysAdmin = profile.is_sysadmin ?? false;
                this._acls = profile.acls ?? [];
                /**
                 * We do not allow to edit Admin's Profile.
                 * Since we removed impersonation in https://returngo.atlassian.net/browse/GLBE-51, the /profile returns
                 * Admin's info, not the merchant's. We stop further initialization here to show the disabled, empty form.
                 */
                if (this._isSysAdmin) {
                    return;
                }
                this._account = new AccountEditorStore(profile, true);
            });
        }
        catch(e) {
            console.error(e);
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    public hasPermission(permission_id: PERMISSION): boolean {
        if (this._acls.length === 0) {
            return false;
        }

        for (const acl of this._acls) {
            const permissions = acl?.role.permissions;
            if (permissions?.some(p => p.permission_id === permission_id || p.permission_id === PERMISSION.ALL)) {
                return true;
            }
        }
        return false;
    }

    public get isSysAdmin(): boolean {
        return this._isSysAdmin;
    }

    public hasRole(roleName: string): boolean {
        return this._acls.some(acl => acl.role.name === roleName);
    }

    public hasManagersApprovePermissions(): boolean {
        return this.hasPermission(PERMISSION.APPROVE_RMA_AS_MANAGER);
    }

    public hasPromptManagersApprovePermissions(): boolean {
        return this.hasPermission(PERMISSION.PROMPT_MANGER_APPROVAL);
    }

    public async logout(): Promise<void> {
        this._iframeService.logout();
    }
}

export default new CurrentAccountStore();
